import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import new_user from '@/js/icons/buyerDashboardWelcomeImage.svg';
import { useNavigate } from 'react-router-dom';
import botIcon from '@/js/icons/botIcon.svg';
import { Bot, ChevronRight, Loader2, Search } from 'lucide-react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/20/solid';
import ProjectCard from '@/js/components/ProjectCard';
import createApiClient from '@/api/axiosInstance';
import { setUserProfile } from '@/js/reduxstore/actions/authActions';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { clearSearchAction } from '@/js/reduxstore/actions/searchActions';

const BuyerDashboardScreen = () => {
  const token = useSelector((state) => state.authReducer.token);
  const { name } = useSelector((state) => state?.authReducer?.userData) || {};
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const apiClient = createApiClient(navigate, dispatch);

  const [loading, setLoading] = useState(false);

  const [description, setDescription] = useState(null);

  // State for the selected sorting option
  const [projects, setProjects] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProjects, setFilteredProjects] = useState([]);

  const first_name = name?.split(' ')?.[0] || '';

  useEffect(() => {
    const getBuyerProfile = async () => {
      try {
        const response = await apiClient.get('/api/buyer-profile/get');
        const responseData = response.data;
        if (responseData?.data) {
          dispatch(setUserProfile(responseData?.data));
        }
      } catch (error) {
        const error_message = error?.response?.data?.error_message;
        console.error('Error: ', error_message || error);
      }
    };

    const getAllProjects = async () => {
      setLoading(true);
      try {
        const response = await apiClient.get('/api/project/getAll');
        const responseData = response.data;
        if (responseData?.data) {
          console.log(responseData?.data);
          setProjects(responseData?.data);
        }
      } catch (error) {
        const error_message = error?.response?.data?.error_message;
        console.error('Error: ', error_message || error);
      } finally {
        setLoading(false);
      }
    };

    getAllProjects();
    getBuyerProfile();
    dispatch(clearSearchAction());
  }, []);

  useEffect(() => {
    if (!projects) return;

    let updatedProjects = [...projects];

    // Filter by project_name
    if (searchTerm.trim()) {
      updatedProjects = updatedProjects.filter((project) =>
        project.project_name.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    // Sort based on selectedOption
    if (selectedOption === 'Name') {
      updatedProjects.sort((a, b) =>
        a.project_name.localeCompare(b.project_name),
      );
    } else if (selectedOption === 'Date') {
      updatedProjects.sort(
        (a, b) => new Date(a.due_date) - new Date(b.due_date),
      );
    }

    setFilteredProjects(updatedProjects);
  }, [searchTerm, selectedOption, projects]);

  // Options for the menu
  const menuOptions = ['Name', 'Date'];

  const handleMenuItemClick = (option) => {
    setSelectedOption(option);
  };

  const handleFindManufacturer = () => {
    if (description === null) {
      alert('Please write description');
      return;
    }

    navigate('/search', {
      replace: true,
      state: { searchDescription: description },
    });
  };

  return (
    <div className="flex flex-col space-y-6 font-roboto px-6">
      {/* Upper Half */}
      <div
        className="w-full mx-auto rounded-lg py-6 px-8"
        style={{
          background:
            'linear-gradient(120deg, #FFF 10.58%, rgba(242, 233, 255, 0.90) 58.47%, #FFF 81.16%)',
          boxShadow: '0px 0px 8px 0px #E2CCFF',
        }}
      >
        <h1 className="text-3xl md:text-4xl font-medium text-gray-700 mb-6">
          Hi {first_name}!
        </h1>

        <div className="flex items-center text-gray-600 text-lg mb-2 gap-2">
          <img src={botIcon} className="w-8 h-8" alt="bot" />
          <span>
            Tell me about your project, and I’ll find the right manufacturers
            for you.
          </span>
        </div>

        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe your manufacturing needs... (e.g., CNC machining for aluminum aerospace parts, ISO 9001 certified, Germany)"
          className="rounded p-4 w-full h-32 text-gray-700 placeholder:text-gray-400 resize-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-darkerPurple border border-darkerPurple shadow"
        />

        <div className="flex justify-center w-full">
          <Button
            className="mt-8 px-3 py-6 bg-darkPurple hover:bg-darkerPurple text-white shadow-md text-[16px]"
            style={{
              background:
                'linear-gradient(86deg, #924CFF 11.29%, #C29AFF 48.32%, #7636D9 98.16%)',
              boxShadow: ' 0px 0px 8px 0px #D2B5FF',
            }}
            onClick={handleFindManufacturer}
          >
            <img src={botIcon} className="w-7 h-7" />
            Find Manufacturer
          </Button>
        </div>
      </div>

      {/* Project Area */}
      <div className="bg-white rounded-lg p-8">
        {/* Options pane */}
        <h1 className="text-2xl mb-8 font-semibold text-gray-600">
          Your Projects
        </h1>
        <div className="flex space-x-4 items-center mb-6">
          <div className="relative w-4/12">
            <span className="absolute inset-y-0 left-3 flex items-center text-gray-400">
              <Search className="h-5 w-5" />
            </span>
            <input
              type="text"
              placeholder="Search Project"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 border-0 bg-gray-100 rounded-lg px-4 py-2 w-full focus:outline-none placeholder:text-sm placeholder:font-semibold"
            />
          </div>
          {/* Sorting Options */}
          <Menu as="div" className="relative inline-block text-left w-2/12">
            <div>
              <MenuButton className="inline-flex w-full justify-between rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-600">
                Sort By {selectedOption && `- ${selectedOption}`}
                <ChevronDownIcon
                  aria-hidden="true"
                  className="-mr-1 size-5 text-gray-500"
                />
              </MenuButton>
            </div>

            <MenuItems
              transition
              className="absolute right-0 z-10 mt-2 w-full origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-150 data-[leave]:duration-100 data-[enter]:ease-out data-[leave]:ease-in"
            >
              <div className="py-1">
                {menuOptions.map((option) => (
                  <MenuItem
                    key={option}
                    as="div"
                    onClick={() => handleMenuItemClick(option)}
                  >
                    <button
                      className={`flex flex-row items-center justify-between w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 ${
                        selectedOption === option
                          ? 'bg-gray-100 text-gray-900'
                          : ''
                      }`}
                    >
                      {option}
                      {selectedOption === option && (
                        <CheckIcon className="inline-block h-4 w-4 ml-2 text-gray-600" />
                      )}
                    </button>
                  </MenuItem>
                ))}
              </div>
            </MenuItems>
          </Menu>
        </div>

        {/* Project Cards Section */}
        {loading ? (
          <div className="col-span-9 w-full h-[300px] bg-white rounded p-4 gap-4">
            <div className="flex flex-row items-center gap-2 text-darkPurple">
              <Loader2 className="h-8 w-8 animate-spin " />
              <span className="text-3xl">Loading</span>
            </div>
          </div>
        ) : (
          <div
            className="grid grid-cols-3 gap-4 p-2"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: '#7636D9 #F1EBFB',
            }}
          >
            {filteredProjects.length > 0 ? (
              filteredProjects.map((project, index) => (
                <ProjectCard key={index} data={project} />
              ))
            ) : (
              <p className="text-gray-500 col-span-3 text-center">
                No matching projects found.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BuyerDashboardScreen;
